// @ts-check
// Campos dos formulários dos blocos (contrato de bots, seção 22.2): texto com o contador de caracteres pelo limite da
// Meta e o seletor de variáveis `{{...}}` (as do sistema e as do bot gravadas antes no caminho), seleção simples,
// escolha de departamento, de membro e de vários membros, e o "Gravar em" com o nome da variável e o campo do CRM.
import { html } from 'htm/preact';
import { useRef, useState } from 'preact/hooks';
import { Dropdown } from '../../ui/dropdown.js';
import { Icon } from '../../ui/icon.js';
import { characterCounter } from '../../lib/bot-message-limits.js';
import { botVariableChoices, insertVariableToken } from '../../lib/bot-variables.js';
import { BOT_RECORD_TARGETS } from '../../../../shared/bots/bot-definition.js';
import { FlowChecklist } from '../flows/node-forms/lead-forms.js';
import { FlowField } from '../flows/flow-ui.js';
import { targetObject } from './bot-lookups.js';
import { translateMetadataLabel, t } from '../../i18n/index.js';

/** Tipos de campo do CRM que uma resposta pode gravar (seção 4.17). */
export const WRITABLE_FIELD_TYPES = Object.freeze(['TEXT', 'NUMBER', 'DATE', 'DATE_TIME', 'EMAILS', 'PHONES', 'SELECT', 'BOOLEAN', 'FULL_NAME', 'RELATION']);
const RELATION_FIELD_NAMES = new Set(['campus', 'course', 'academicPeriod']);
// etapa e etiquetas têm blocos próprios
const BLOCKED_FIELD_NAMES = new Set(['stage', 'tags', 'effectiveStage', 'stageOverride']);

let fieldSequence = 0;
/** Id estável de um campo do formulário. @param {string} prefix */
export function useFieldId(prefix) {
  const ref = useRef(/** @type {string | null} */ (null));
  if (!ref.current) ref.current = `${prefix}-${(fieldSequence += 1)}`;
  return ref.current;
}

/**
 * Botão "{{ }}" com a lista de variáveis; escolher insere no campo.
 * @param {{ variables: string[], onPick: (token: string) => void, label?: string }} props
 */
export function BotVariablePicker({ variables, onPick, label }) {
  const anchorRef = useRef(/** @type {HTMLButtonElement | null} */ (null));
  const [isOpen, setIsOpen] = useState(false);
  const choices = botVariableChoices(variables);
  const groups = [...new Set(choices.map((choice) => choice.group))];
  return html`<button type="button" ref=${anchorRef} class="bot-variable-button" aria-haspopup="dialog" aria-expanded=${isOpen ? 'true' : 'false'}
      title=${label ?? t('bots.forms.insertVariable')} aria-label=${label ?? t('bots.forms.insertVariable')} onClick=${() => setIsOpen(!isOpen)}>
      <${Icon} name="IconBraces" size=${14} />
    </button>
    <${Dropdown} anchorRef=${anchorRef} isOpen=${isOpen} onClose=${() => setIsOpen(false)} placement="bottom-end" width=${280} role="dialog">
      <div class="bot-variable-menu" aria-label=${t('bots.forms.insertVariable')}>
        ${groups.map((group) => html`<section key=${group} class="bot-variable-group">
          <h3 class="bot-variable-group-title">${t(`bots.variables.groups.${group}`)}</h3>
          <ul>${choices.filter((choice) => choice.group === group).map((choice) => html`<li key=${choice.token}>
            <button type="button" class="bot-variable-item" onClick=${() => {
              setIsOpen(false);
              onPick(choice.token);
            }}>
              <span>${group === 'bot' ? choice.name : t(`bots.variables.names.${group}.${choice.name}`)}</span>
              <code>${choice.token}</code>
            </button>
          </li>`)}</ul>
        </section>`)}
      </div>
    <//>`;
}

/**
 * Texto com contador e variáveis (linha única ou várias linhas).
 * @param {{ label: string, value: string | null | undefined, onChange: (value: string) => void, maximum?: number | null, variables?: string[] | null,
 *   multiline?: boolean, rows?: number, placeholder?: string, hint?: string | null, error?: string | null, testId?: string }} props
 */
export function BotTextField({ label, value, onChange, maximum = null, variables = null, multiline = false, rows = 3, placeholder, hint = null, error = null, testId }) {
  const id = useFieldId('bot-text');
  const inputRef = useRef(/** @type {HTMLInputElement | HTMLTextAreaElement | null} */ (null));
  const text = value ?? '';
  const counter = maximum ? characterCounter(text, maximum) : null;
  const insert = (/** @type {string} */ token) => {
    const element = inputRef.current;
    const result = insertVariableToken(text, token, element ? element.selectionStart : null);
    onChange(result.text);
    requestAnimationFrame(() => {
      element?.focus();
      element?.setSelectionRange(result.cursor, result.cursor);
    });
  };
  const counterError = counter?.isOver ? t('bots.forms.overLimit', { maximum: counter.maximum }) : null;
  const handleInput = (/** @type {InputEvent} */ event) => onChange(/** @type {HTMLInputElement} */ (event.currentTarget).value);
  return html`<${FlowField} label=${label} htmlFor=${id} error=${error ?? counterError} hint=${hint} className="bot-text-field">
    <div class="bot-text-control">
      ${multiline
        ? html`<textarea id=${id} ref=${inputRef} class="text-input bot-textarea" rows=${rows} value=${text} placeholder=${placeholder} onInput=${handleInput}
            aria-invalid=${error || counterError ? 'true' : undefined} data-testid=${testId}></textarea>`
        : html`<input id=${id} ref=${inputRef} class="text-input" value=${text} placeholder=${placeholder} onInput=${handleInput}
            aria-invalid=${error || counterError ? 'true' : undefined} data-testid=${testId} />`}
      ${variables ? html`<${BotVariablePicker} variables=${variables} onPick=${insert} />` : null}
    </div>
    ${counter ? html`<span class="bot-counter" data-over=${counter.isOver ? 'true' : undefined} aria-live="polite">${counter.text}</span>` : null}
  <//>`;
}

/**
 * Seleção simples com o rótulo.
 * @param {{ label: string, value: string | null | undefined, options: Array<{ value: string, label: string }>, onChange: (value: string | null) => void,
 *   placeholder?: string | null, error?: string | null, hint?: string | null, testId?: string }} props
 */
export function BotSelectField({ label, value, options, onChange, placeholder = null, error = null, hint = null, testId }) {
  const id = useFieldId('bot-select');
  return html`<${FlowField} label=${label} htmlFor=${id} error=${error} hint=${hint}>
    <select id=${id} class="text-input" value=${value ?? ''} data-testid=${testId} aria-invalid=${error ? 'true' : undefined}
      onChange=${(/** @type {Event} */ event) => onChange(/** @type {HTMLSelectElement} */ (event.currentTarget).value || null)}>
      ${placeholder !== null ? html`<option value="">${placeholder ?? t('bots.forms.choose')}</option>` : null}
      ${options.map((option) => html`<option key=${option.value} value=${option.value}>${option.label}</option>`)}
    </select>
  <//>`;
}

/**
 * Número inteiro com mínimo e máximo.
 * @param {{ label: string, value: number | null | undefined, onChange: (value: number | null) => void, minimum?: number, maximum?: number, hint?: string | null,
 *   error?: string | null, placeholder?: string, suffix?: string | null }} props
 */
export function BotNumberField({ label, value, onChange, minimum, maximum, hint = null, error = null, placeholder, suffix = null }) {
  const id = useFieldId('bot-number');
  return html`<${FlowField} label=${label} htmlFor=${id} error=${error} hint=${hint}>
    <div class="bot-number-control">
      <input id=${id} type="number" class="text-input" value=${value ?? ''} min=${minimum} max=${maximum} placeholder=${placeholder}
        onInput=${(/** @type {InputEvent} */ event) => {
          const raw = /** @type {HTMLInputElement} */ (event.currentTarget).value;
          onChange(raw === '' ? null : Math.trunc(Number(raw)));
        }} />
      ${suffix ? html`<span class="bot-number-suffix">${suffix}</span>` : null}
    </div>
  <//>`;
}

/**
 * Vários membros com busca.
 * @param {{ label: string, members: Array<{ id: string, label: string }>, selected: string[], onChange: (ids: string[]) => void, error?: string | null, maximum?: number }} props
 */
export function BotMembersField({ label, members, selected, onChange, error = null, maximum }) {
  return html`<${FlowField} label=${label} error=${error}>
    <${FlowChecklist} items=${members} selected=${selected} onChange=${onChange} label=${label} maximum=${maximum} emptyText=${t('bots.forms.noMembers')} />
  <//>`;
}

/**
 * "Gravar em": nome da variável do bot e, opcional, o campo do CRM (alvo e campo, só os tipos aceitos).
 * @param {{ value: { variable?: string | null, target?: string | null, fieldName?: string | null } | null | undefined,
 *   onChange: (value: { variable: string, target: string | null, fieldName: string | null }) => void, variableError?: string | null, allowField?: boolean }} props
 */
export function BotSaveToField({ value, onChange, variableError = null, allowField = true }) {
  const current = { variable: value?.variable ?? '', target: value?.target ?? null, fieldName: value?.fieldName ?? null };
  const id = useFieldId('bot-save-to');
  const object = current.target ? targetObject(current.target) : null;
  const fields = (object?.fields ?? []).filter((/** @type {any} */ field) => field.isActive !== false && !field.isSystem && !BLOCKED_FIELD_NAMES.has(field.name)
    && WRITABLE_FIELD_TYPES.includes(field.type) && (field.type !== 'RELATION' || RELATION_FIELD_NAMES.has(field.name)));
  return html`<div class="bot-save-to">
    <${FlowField} label=${t('bots.forms.saveTo.variable')} htmlFor=${id} error=${variableError} hint=${t('bots.forms.saveTo.variableHint')}>
      <div class="bot-variable-name">
        <span class="bot-variable-prefix" aria-hidden="true">bot.</span>
        <input id=${id} class="text-input" value=${current.variable} placeholder=${t('bots.forms.saveTo.variablePlaceholder')}
          onInput=${(/** @type {InputEvent} */ event) => onChange({ ...current, variable: /** @type {HTMLInputElement} */ (event.currentTarget).value.replace(/[^a-zA-Z0-9]/g, '') })} />
      </div>
    <//>
    ${allowField
      ? html`<div class="bot-form-row">
          <${BotSelectField} label=${t('bots.forms.saveTo.target')} value=${current.target} placeholder=${t('bots.forms.saveTo.noField')}
            options=${BOT_RECORD_TARGETS.map((target) => ({ value: target, label: t(`bots.recordTargets.${target}`) }))}
            onChange=${(target) => onChange({ ...current, target, fieldName: null })} />
          ${current.target
            ? html`<${BotSelectField} label=${t('bots.forms.saveTo.field')} value=${current.fieldName}
                options=${fields.map((/** @type {any} */ field) => ({ value: field.name, label: translateMetadataLabel(field.label) }))}
                onChange=${(fieldName) => onChange({ ...current, fieldName })} />`
            : null}
        </div>`
      : null}
  </div>`;
}
